/**
 * @file Login.tsx
 * @description Merchant login page. Simple email/password form that authenticates via
 * Supabase Auth and redirects to the dashboard on success.
 *
 * How it works:
 * - Uses useAuth hook for signIn (Supabase signInWithPassword)
 * - On successful sign-in, navigates to /dashboard
 * - Displays inline error messages for failed attempts
 * - Links to registration page for new merchants
 *
 * @changelog
 * - 2026-03-19: Added documentation header
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ArrowRight } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <Link to="/" className="font-mono text-bone-white tracking-widest text-sm font-medium uppercase">
            BONESPAY
          </Link>
          <p className="mt-3 font-sans text-sm text-navy-200/50">
            Sign in to your merchant dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="border border-red-500/30 bg-red-500/10 rounded-subtle px-4 py-3">
              <p className="font-sans text-sm text-red-300">{error}</p>
            </div>
          )}

          <div>
            <label className="block font-mono text-[10px] text-bone-white/40 uppercase tracking-widest mb-2">
              Email
            </label>
            <input
              type="email"
              required
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-navy-700/40 rounded-subtle px-4 py-3 bg-navy-900/30 text-bone-white font-sans text-sm focus:outline-none focus:border-navy-500 transition-colors placeholder:text-bone-white/20"
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] text-bone-white/40 uppercase tracking-widest mb-2">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-navy-700/40 rounded-subtle px-4 py-3 bg-navy-900/30 text-bone-white font-sans text-sm focus:outline-none focus:border-navy-500 transition-colors placeholder:text-bone-white/20"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-bone-white text-navy-900 font-mono text-sm font-medium py-3.5 rounded-subtle hover:bg-navy-100 transition-colors disabled:opacity-50 uppercase tracking-wider flex items-center justify-center gap-2 group"
          >
            {loading ? (
              'Signing in...'
            ) : (
              <>
                Sign In
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>

        <p className="mt-8 text-center font-sans text-sm text-bone-white/30">
          No account?{' '}
          <Link to="/register" className="text-bone-white/60 hover:text-bone-white transition-colors">
            Create one
          </Link>
        </p>

        <div className="mt-10 border-t border-navy-700/20 pt-6">
          <p className="font-mono text-[10px] text-bone-white/15 uppercase tracking-widest text-center">
            The blockchain never forgets.
          </p>
        </div>
      </div>
    </div>
  );
}
